import { id } from "date-fns/locale";
import { muiDataGridStarted } from "../apiutils";
import { apiSlice } from "../createApiSlice";

const PRODUCT_BASE = "/products";
const buildUrl = (endpoint) => PRODUCT_BASE + endpoint;

const productsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getProducts: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: 'Products' }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getProducts', 'Products')
        }),
        getProductSerialnumbers: builder.query({
            query: (body) => ({
                url: buildUrl(`/serialnumbers`),
                method: 'POST',
                body: body,
            }),
            providesTags: () => [{ type: 'ProductSerialNumber' }],
        }),
        getActiveProductSerialnumbers: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/serialnumbers/active?page=${args.page}&size=${args.size}`),
                method: 'POST',
                body: args.body,
            }),
            providesTags: () => [{ type: 'ActiveProductSerialNumber' }],
            onQueryStarted: muiDataGridStarted.bind(null, 'getActiveProductSerialNumber', 'ActiveProductSerialNumber')
        }),
        getProductsSelect: builder.query({
            query: filterData => ({
                url: buildUrl(`/select`),
                method: 'POST',
                body: filterData,
            }),
            providesTags: (args) => [{ type: 'ProductsSelect', id: args?.webshopcredentialsId ?? 0 }],
        }),
        getProductsSelectForRelation: builder.query({
            query: filterData => ({
                url: buildUrl(`/select`),
                method: 'POST',
                body: filterData,
            }),
            providesTags: (args) => [{ type: 'ProductsSelect'}],
        }),
        getSuggestedLocations:builder.query({
            query:id => ({
                url:buildUrl(`/locations/${id}`),
                method: "GET",
            }),
            providesTags: (args) => [{ type: 'ProductLocations'}],
        }),
        getFilteredSuggestedLocations:builder.query({
            query:({productId, warehouseId, filters}) => ({
                url:buildUrl(`/locations/${productId}`),
                method: "POST",
                body: filters ?? {},
                headers: {
                    ...(warehouseId ? { warehouse_id: warehouseId } : {}),
                },
            }),
            providesTags: (args) => [{ type: 'ProductLocations'}],
        }),
        getProduct: builder.query({
            query: (id) => ({
                url: buildUrl(`/${id}`),
                method: 'GET',
            }),
            providesTags: (result, error, id) => [{ type: 'ProductsSingle'}],
            // onQueryStarted: muiDataGridStarted.bind(null, 'getProduct', 'Product')
        }),
        updateProduct: builder.mutation({
            query: (productInfo) => ({
                url: buildUrl(`/${productInfo.id}`),
                method: 'PUT',
                body: productInfo,
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Products'},{ type: 'ProductsSingle'},{ type: 'LogsSingle'}],
        }),
        deleteProduct: builder.mutation({
            query: id => ({
                url: buildUrl(`/${id}`),
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Products'}, { type: 'ProductsSingle'}],
        }),
        updateProductRelation: builder.mutation({
            query: ({ id, relationId }) => ({
                url: buildUrl(`/${id}/relation`),
                method: 'PUT',
                body: { relationId },
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Products'}, { type: 'ProductsSingle'}, { type: 'LogsSingle'}],
        }),
        convertProductToNormal: builder.mutation({
            query: id => ({
                url: buildUrl(`/${id}/convert-to-normal`),
                method: 'PUT',
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'ChildProducts'}, { type: 'Products'}, { type: 'ProductsSingle'}],
        }),
        getChildProducts: builder.query({
            query: ({ args, invalidate }) => ({
                url: buildUrl(`/${args.id}/childproducts`),
                method: 'GET',
            }),
            providesTags: (result, error, id) => [{ type: 'ChildProducts'}],
            onQueryStarted: muiDataGridStarted.bind(null, 'getChildProducts', 'ChildProducts')
        }),
        deleteChildProducts: builder.mutation({
            query: args => ({
                url: buildUrl(`/${args.productId}/childproducts`),
                method: 'DELETE',
                body: args.body,
            }),
            invalidatesTags: (result, error, arg) => [{type: 'ChildProducts'}, { type: 'Products' }],
        }),
        addChildProducts: builder.mutation({
            query: args => ({
                url: buildUrl(`/${args.productId}/childproducts`),
                method: 'POST',
                body: args.body,
            }),
            invalidatesTags: (result, error, arg) => [{type: 'ChildProducts'}, { type: 'Products' }],
        }),
        updateChildProduct: builder.mutation({
            query: childProductInfo => ({
                url: buildUrl(`/${childProductInfo.parentProductId}/childproducts/${childProductInfo.childProductId}`),
                method: 'PUT',
                body: childProductInfo,
            }),
            invalidatesTags: (result, error, arg) => [{type: 'ChildProducts'}, { type: 'Products' }],
        }),
        addProduct: builder.mutation({
            query: productInfo => ({
                url: buildUrl(`/add`),
                method: 'POST',
                body: productInfo,
            }),
            invalidatesTags: (result, error, arg) => [{type: 'Products'}],
        }),
        getRelationDashboardProducts: builder.query({
            query: (body) => ({
                url: buildUrl(`/dashboard/relation/products`),
                method: 'POST',
                body
            }),
        }),
        // Inkoopadvies grid rows. Driven by ShDataGrid3 ({ args: { page, size, body } }).
        // The service wraps its payload in { response: … } and SupplierFilter writes -1
        // for "all suppliers" — both are normalised here so the grid sees its usual shape.
        // The grid's sort is dropped: the rows arrive pre-sorted by restock urgency. The
        // searchString passes through and filters on SKU or description.
        getStockForecast: builder.query({
            query: ({ args }) => {
                const { sort, supplierId, ...rest } = args.body;
                return {
                    url: buildUrl(`/forecast/stock?page=${args.page}&size=${args.size}`),
                    method: 'POST',
                    body: {
                        ...rest,
                        ...(parseInt(supplierId) > 0 ? { supplierId: parseInt(supplierId) } : {}),
                    },
                };
            },
            transformResponse: (response) => {
                const payload = response?.response ?? response;
                const summary = response?.summary ?? payload?.summary;
                // Forecast rows are keyed by productId; ShDataGrid3 selection keys by row.id, so mirror it.
                const data = (payload?.data ?? []).map((row) => ({ ...row, id: row.productId }));
                return { ...payload, data, summary };
            },
            // Open inbounds count towards the advice, so any inbound mutation changes these rows.
            providesTags: () => [{ type: 'StockForecast' }],
        }),
        getRelationDashboardProductsForRelation: builder.query({
            query: (body) => ({
                url: buildUrl(`/dashboard/relation/products`),
                method: 'POST',
                body
            }),
            providesTags: (result, error, arg) => [{ type: 'RelationDashboardProducts', id: arg?.relationId }],
        }),
        getProductTotals: builder.query({
            query: type => ({
                url: buildUrl(`/totals/${type}`),
                method: 'GET',
            }),
        }),
        exportProducts: builder.mutation({
            query: (body) => ({
                url: buildUrl(`/export`),
                method: 'POST',
                body,
                responseHandler: async (response) => {
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }

                    const contentType = response.headers.get('content-type');

                    // Check if response is JSON (status 202 - processing or 200 with blobUrl)
                    if (contentType && contentType.includes('application/json')) {
                        const jsonData = await response.json();

                        // If immediate download is ready (status 200 with blobUrl)
                        if (jsonData.blobUrl) {
                            // For storage URLs or signed URLs, just trigger direct download
                            // No need to fetch again - the URL is already accessible
                            const a = document.createElement('a');
                            a.href = jsonData.blobUrl;
                            a.download = jsonData.fileName || `products_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                            a.target = '_blank'; // Open in new tab as fallback
                            document.body.appendChild(a);
                            a.click();
                            a.remove();

                            return { data: { success: true, message: jsonData.message, immediate: true } };
                        }

                        // Background processing (status 202)
                        return { data: { success: true, processing: true, message: jsonData.message, exportId: jsonData.exportId } };
                    }

                    // Legacy support: Direct blob response (for backwards compatibility)
                    const blob = await response.blob();

                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `products_export_${new Date().toISOString().split('T')[0]}.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);

                    return { data: { success: true, immediate: true } };
                },
            }),
        }),
    })
});

export const {
    useGetProductQuery,
    useGetProductsQuery,
    useGetProductSerialnumbersQuery,
    useGetProductsSelectQuery,
    useUpdateProductMutation,
    useGetProductTotalsQuery,
    useGetChildProductsQuery,
    useDeleteChildProductsMutation,
    useAddChildProductsMutation,
    useUpdateChildProductMutation,
    useAddProductMutation,
    useDeleteProductMutation,
    useConvertProductToNormalMutation,
    useUpdateProductRelationMutation,
    useGetRelationDashboardProductsQuery,
    useGetStockForecastQuery,
    useGetRelationDashboardProductsForRelationQuery,
    useGetProductsSelectForRelationQuery,
    useExportProductsMutation,
    useGetSuggestedLocationsQuery,
    useGetFilteredSuggestedLocationsQuery,
    useGetActiveProductSerialnumbersQuery
} = productsApiSlice;
